import { useEffect, useRef, useState } from 'react'
import type { QuickBooksFpaMlModelRun, ServerApiClient } from '../../data/ServerApiClient'
import { accessSessionIdentity } from '../../data/accessSessionIdentity'

const clean = (value: any): string => String(value ?? '').trim()

const formatDateTime = (value: any): string => {
  const text = clean(value)
  if (!text) return '-'
  const date = new Date(text)
  if (Number.isNaN(date.getTime())) return text
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const statusColor = (status: any): string => {
  const text = clean(status).toLowerCase()
  if (text === 'completed' || text === 'ready') return '#047857'
  if (text === 'running' || text === 'queued' || text === 'generating') return '#0369a1'
  if (text === 'insufficient_training_data') return '#92400e'
  if (text === 'failed') return '#991b1b'
  return '#475467'
}

const formatStatus = (status: any): string => clean(status).replace(/_/g, ' ') || 'not started'

const modelRunLabel = (run: QuickBooksFpaMlModelRun): string => {
  const kind = clean(run.run_kind).replace(/_/g, ' ') || 'model run'
  return `${kind} | ${formatStatus(run.status)}`
}

type ModelReviewRunsProps = {
  runs: QuickBooksFpaMlModelRun[]
  busy: string
  pendingRunIds?: ReadonlySet<string>
  onReview: (run: QuickBooksFpaMlModelRun, status: string, note: string) => Promise<void>
  onEvidence?: (run: QuickBooksFpaMlModelRun) => void
  emptyMessage?: string
}

// Both Forecast surfaces use the existing review controls and disclosures.
export function ModelReviewRuns({ runs, busy, pendingRunIds, onReview, onEvidence, emptyMessage = 'No model review runs have been recorded yet.' }: ModelReviewRunsProps) {
  return runs.length ? runs.map((run) => (
        <div key={run.id} style={{ padding: '10px 12px', borderBottom: '1px solid #eef2f0', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', gap: 10 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ color: '#111827', fontSize: 12, fontWeight: 900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{modelRunLabel(run)}</div>
            <div style={{ marginTop: 3, color: '#667085', fontSize: 11, fontWeight: 700 }}>
              {formatDateTime(run.finished_at || run.started_at || run.created_at)} | review {formatStatus(run.review_status || 'unreviewed')}
            </div>
            {run.economic_policy_review ? <div style={{ marginTop: 8, fontSize: 12 }}>
              <strong>Valuation financial assumptions</strong>
              <div>{run.economic_policy_review.framework} · {run.economic_policy_review.accounting_basis} · {run.economic_policy_review.currency_code}</div>
              <div>Training through {run.economic_policy_review.training_end}</div>
              <div>{run.economic_policy_review.manual_treatment}</div>
              {run.economic_policy_review.interest_timing ? <div>{run.economic_policy_review.interest_timing}</div> : null}
              {run.economic_policy_review.tax_shield ? <div>{run.economic_policy_review.tax_shield}</div> : null}
              <ul>{run.economic_policy_review.assumptions.map((assumption, index) => <li key={index}>{assumption.account}: {assumption.treatment}</li>)}</ul>
              <div>{run.economic_policy_review.qualification}</div>
            </div> : run.run_kind === 'current_native_fit' ? <div>No valuation economic policy was supplied. Forecast approval does not add one.</div> : null}
            {run.backtest_json && Object.keys(run.backtest_json || {}).length ? <div style={{ marginTop: 4, color: '#667085', fontSize: 11, fontWeight: 700 }}>Backtest: {clean(run.backtest_json.method || run.backtest_json.note || 'available')}</div> : null}
            <div style={{ marginTop: 7, display: 'flex', gap: 5 }}>
              <button type="button" disabled={pendingRunIds?.has(run.id) || busy === `model:${run.id}:approved`} onClick={() => onReview(run, 'approved', 'Model run approved.')} style={{ minHeight: 26, border: '1px solid #bbf7d0', borderRadius: 6, background: '#f0fdf4', color: '#047857', padding: '3px 7px', fontSize: 11, fontWeight: 850 }}>Approve run</button>
              <button type="button" disabled={pendingRunIds?.has(run.id) || busy === `model:${run.id}:needs_work`} onClick={() => onReview(run, 'needs_work', 'Model run rejected for review.')} style={{ minHeight: 26, border: '1px solid #fed7aa', borderRadius: 6, background: '#fff7ed', color: '#9a3412', padding: '3px 7px', fontSize: 11, fontWeight: 850 }}>Reject run</button>
              {onEvidence ? <button type="button" onClick={() => onEvidence(run)} style={{ minHeight: 26, border: '1px solid #d1d5db', borderRadius: 6, background: '#ffffff', color: '#344054', padding: '3px 7px', fontSize: 11, fontWeight: 850 }}>Evidence</button> : null}
            </div>
          </div>
          <span style={{ color: statusColor(run.status), fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>{formatStatus(run.status)}</span>
        </div>
      )) : <div style={{ padding: 18, color: '#667085', fontSize: 12, fontWeight: 750 }}>{emptyMessage}</div>
}

type CurrentModelReviewProps = {
  appApi: ServerApiClient
  workbookId: string
  refreshKey: number
  onReviewSaved: () => void
}

export function QuickBooksForecastModelReview({ appApi, workbookId, refreshKey, onReviewSaved }: CurrentModelReviewProps) {
  const sessionIdentity = accessSessionIdentity(appApi.getToken?.())
  const scope = `${workbookId}|${sessionIdentity}`
  const liveScope = useRef(scope)
  liveScope.current = scope
  const alive = useRef(true)
  const loadSequence = useRef(0)
  const reviewSequence = useRef(0)
  const pendingReviews = useRef(new Set<string>())
  const [pendingRunIds, setPendingRunIds] = useState<ReadonlySet<string>>(new Set())
  const [refreshNonce, setRefreshNonce] = useState(0)
  const [loadedScope, setLoadedScope] = useState('')
  const [runs, setRuns] = useState<QuickBooksFpaMlModelRun[]>([])
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [scope])

  useEffect(() => {
    let cancelled = false
    const sequence = ++loadSequence.current
    ++reviewSequence.current
    const current = () => !cancelled && alive.current && loadSequence.current === sequence
      && liveScope.current === scope && accessSessionIdentity(appApi.getToken?.()) === sessionIdentity
    setLoadedScope('')
    setRuns([])
    setBusy('')
    setError('')
    setMessage('')
    setLoading(true)
    void appApi.getQuickBooksFpaMlModelReview(workbookId).then(response => {
      if (!current()) return
      setRuns(response.model_runs.filter(run => run.run_kind === 'current_native_fit' && run.workbook_id === workbookId))
      setLoadedScope(scope)
    }).catch((err: unknown) => {
      if (!current()) return
      setError(err instanceof Error ? err.message : String(err))
      setLoadedScope(scope)
    }).finally(() => {
      if (current()) setLoading(false)
    })
    return () => { cancelled = true }
  }, [appApi, workbookId, scope, sessionIdentity, refreshKey, refreshNonce])

  const visibleRuns = loadedScope === scope ? runs : []
  const updateReview = async (run: QuickBooksFpaMlModelRun, status: string, note: string) => {
    if (!alive.current || loadedScope !== scope || liveScope.current !== scope
      || accessSessionIdentity(appApi.getToken?.()) !== sessionIdentity
      || !visibleRuns.some(item => item.id === run.id) || pendingReviews.current.has(run.id)) return
    // The synchronous fence survives list/session refresh until this PATCH settles.
    pendingReviews.current.add(run.id)
    setPendingRunIds(new Set(pendingReviews.current))
    const sequence = ++reviewSequence.current
    const sameScope = () => alive.current && liveScope.current === scope
      && accessSessionIdentity(appApi.getToken?.()) === sessionIdentity
    const current = () => sameScope() && reviewSequence.current === sequence
    setBusy(`model:${run.id}:${status}`)
    setError('')
    setMessage('')
    try {
      const updated = await appApi.updateQuickBooksFpaMlModelRunReview(workbookId, run.id, { status, note })
      if (!sameScope()) return
      if (updated.id !== run.id || updated.workbook_id !== run.workbook_id
        || updated.connection_id !== run.connection_id || updated.realm_id !== run.realm_id) {
        if (current()) setError('The review response did not match this model run. Refresh model review to continue.')
        return
      }
      if (!current()) {
        // A same-scope refresh invalidated this ACK; re-read the saved state.
        setRefreshNonce(value => value + 1)
        onReviewSaved()
        return
      }
      setRuns(items => items.map(item => item.id === updated.id ? updated : item))
      setMessage('Model review saved. Forecast availability is checked against your current books.')
      onReviewSaved()
    } catch (err: unknown) {
      if (current()) setError(err instanceof Error ? err.message : String(err))
    } finally {
      pendingReviews.current.delete(run.id)
      if (alive.current) setPendingRunIds(new Set(pendingReviews.current))
      if (current()) setBusy('')
    }
  }

  return <section aria-label="Forecast model review" style={{ margin: '16px 0', border: '1px solid #e5e7eb', borderRadius: 8 }}>
    <div style={{ padding: 12 }}>
      <h2 style={{ margin: '0 0 6px' }}>Model review</h2>
      <p>Fit a model, review its results, then approve or reject the run. Forecasts still require a model fitted to your current books.</p>
      <button type="button" className="btn" disabled={loading} onClick={() => setRefreshNonce(value => value + 1)}>Refresh model review</button>
      {loading ? <div role="status">Loading model review…</div> : null}
      {loadedScope === scope && error ? <div role="alert">{error}</div> : null}
      {loadedScope === scope && message ? <div role="status">{message}</div> : null}
    </div>
    {!loading ? <ModelReviewRuns runs={visibleRuns} busy={busy} pendingRunIds={pendingRunIds} onReview={updateReview}
      emptyMessage="No current model review runs have been recorded yet. Fit a model, then refresh this list after the run finishes." /> : null}
  </section>
}
